import { environment } from '../../environments/environment';

/**
 * Per-service base URLs, keyed by the path prefix each service is mounted on.
 *
 * In the deployed environments every backend sits behind one host, so `apiUrl` alone was
 * enough. Running a service locally breaks that: the services listen on different ports, and
 * pointing `apiUrl` at one of them sends every other service's traffic to the wrong app.
 *
 * An empty entry falls back to `apiUrl`, so this is inert in every deployed environment and
 * only takes effect when someone sets a local override.
 */
const SERVICE_BASE_URLS: ReadonlyArray<[string, string]> = [
    ['api/user', environment.userApiUrl],
    ['api/master', environment.masterApiUrl],
    ['api/digital-twin', environment.digitalTwinApiUrl],
    ['api/voyage', environment.voyageApiUrl],
    ['api/sensor-engine', environment.sensorengine],
];

/**
 * Resolves the host for an API path, in precedence order:
 *   1. an explicit base URL supplied by the caller
 *   2. the per-service override matching the path prefix, when it is set
 *   3. environment.apiUrl
 *
 * Lives outside HttpService so that callers using HttpClient directly resolve hosts the same
 * way. Anything that builds a URL from `environment.apiUrl` by hand bypasses the overrides.
 */
export function resolveApiBaseUrl(url: string, explicitBaseUrl?: string | null): string {
    if (explicitBaseUrl) {
        return explicitBaseUrl;
    }

    const path = (url || '').replace(/^\/+/, '');
    const match = SERVICE_BASE_URLS.find(
        ([prefix, host]) => !!host && path.startsWith(prefix)
    );

    return match ? match[1] : environment.apiUrl;
}
